
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Language, Theme } from '../types';
import { GlobeIcon, SunIcon, MoonIcon, AiIcon, ListIcon, CloseIcon, SearchIcon } from './icons';
import { t } from '../translations';
import { useMenu } from '../src/contexts/MenuContext';
import { useUser } from '../contexts/UserContext';
import { useScrollLock } from '../src/utils/useScrollLock';
import { Link } from 'react-router-dom';
import { Settings as SettingsIcon, Bell, HandPlatter, Receipt, Sparkles, BookOpen, SprayCan } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { ServiceType } from '../types';
import { toast } from 'sonner';
import { isRateLimited } from '../src/utils/rateLimitedWrite';

interface BottomDockProps {
  currentLanguage: Language;
  onLanguageChange: (lang: Language) => void;
  currentTheme: Theme;
  onThemeChange: () => void;
  onAiClick: () => void;
  onSelectCategory: (id: string) => void;
  tableNumber: string;
}

const languages: { code: Language; name: string }[] = [
  { code: 'ky', name: 'Кыргызча' },
  { code: 'ru', name: 'Русский' },
  { code: 'zh', name: '中文' },
  { code: 'en', name: 'English' },
];

const BottomDock: React.FC<BottomDockProps> = ({
    currentLanguage,
    onLanguageChange,
    currentTheme,
    onThemeChange,
    onAiClick,
    onSelectCategory,
    tableNumber
}) => {
  const [isLangSheetOpen, setIsLangSheetOpen] = useState(false);
  const [isCategorySheetOpen, setIsCategorySheetOpen] = useState(false);
  const [isServiceSheetOpen, setIsServiceSheetOpen] = useState(false);
  const { sendServiceRequest } = useCart();
  // Живое меню из Firestore, не статичный data/menuData — иначе новые
  // категории не попадают в док, а счётчики блюд устаревают.
  const { menuData } = useMenu();

  useScrollLock(isLangSheetOpen || isCategorySheetOpen || isServiceSheetOpen);

  // Visual: активная вкладка получает большую оранжевую подложку и slightly
  // larger иконку — это даёт «filled vs outline» эффект без необходимости
  // импортировать вторую версию каждой иконки.
  const navItemClass = "relative flex flex-col items-center justify-center w-full h-full gap-1 active:scale-90 transition-transform duration-200";
  const labelClass = "text-[10px] font-semibold leading-none tracking-tight";
  const pillClass = (active: boolean) =>
    `flex items-center justify-center rounded-2xl transition-all ${
      active ? 'bg-accent text-white p-2.5 shadow-md shadow-accent-20' : 'p-2 text-ink-soft'
    }`;
  const iconClass = (active: boolean) => (active ? 'w-6 h-6' : 'w-5 h-5');

  const { role } = useUser();

  const handleServiceAction = async (type: ServiceType) => {
    if (!tableNumber) {
      toast.error(t('cashier.tableRequired', currentLanguage));
      return;
    }
    try {
      await sendServiceRequest(type, tableNumber);
      setIsServiceSheetOpen(false);
      toast.success(`${t(`service.${type}`, currentLanguage)} - ${t('service.received', currentLanguage)}`);
    } catch (e) {
      toast.error(
        isRateLimited(e) ? t('service.tooFast', currentLanguage) : t('cart.error', currentLanguage)
      );
    }
  };

  // Цвета мапятся на статусные токены — корректно работают в dark mode.
  const serviceActions: { type: ServiceType; label: string; icon: React.ReactNode; color: string }[] = [
    { type: 'waiter', label: t('service.waiter', currentLanguage), icon: <HandPlatter className="w-5 h-5" />, color: 'bg-accent-10 text-accent' },
    { type: 'bill', label: t('service.bill', currentLanguage), icon: <Receipt className="w-5 h-5" />, color: 'bg-success-bg-50 text-success-fg' },
    { type: 'napkins', label: t('service.napkins', currentLanguage), icon: <Sparkles className="w-5 h-5" />, color: 'bg-info-bg-50 text-info-fg' },
    { type: 'menu', label: t('service.menu', currentLanguage), icon: <BookOpen className="w-5 h-5" />, color: 'bg-role-bg text-role-fg' },
    { type: 'clean', label: t('service.clean', currentLanguage), icon: <SprayCan className="w-5 h-5" />, color: 'bg-warning-bg-50 text-warning-fg' },
  ];

  return (
    <>
      {/* Fixed Bottom Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--bg-card-90)] backdrop-blur-2xl border-t border-[var(--glass-border)] min-h-[80px] pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
        <div className="flex items-center justify-around h-full max-w-md mx-auto px-4">
            
            {/* 1. Menu Categories */}
            <button
                onClick={() => {
                    setIsCategorySheetOpen(true);
                    setIsLangSheetOpen(false);
                }}
                className={`${navItemClass} ${isCategorySheetOpen ? 'text-accent' : ''}`}
            >
                <div className={pillClass(isCategorySheetOpen)}>
                    <ListIcon className={iconClass(isCategorySheetOpen)} />
                </div>
                <span className={labelClass}>{t('dock.menu', currentLanguage)}</span>
            </button>

            {/* 2. Service */}
            <button
                onClick={() => {
                    setIsServiceSheetOpen(true);
                    setIsCategorySheetOpen(false);
                    setIsLangSheetOpen(false);
                }}
                className={`${navItemClass} ${isServiceSheetOpen ? 'text-accent' : ''}`}
            >
                <div className={pillClass(isServiceSheetOpen)}>
                    <HandPlatter className={iconClass(isServiceSheetOpen)} />
                </div>
                <span className={labelClass}>{t('dock.service', currentLanguage)}</span>
            </button>

            {/* 3. AI Assistant — всегда выделена акцентной заливкой,
                 plus pulse-dot как индикатор «интеллекта». */}
            <button
                onClick={onAiClick}
                className={`${navItemClass} text-accent`}
                aria-label="AI assistant"
            >
                <div className="relative flex items-center justify-center p-2.5 rounded-2xl bg-accent text-white shadow-md shadow-accent-20">
                    <AiIcon className="w-6 h-6" />
                    <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                </div>
                <span className={`${labelClass} font-bold`}>AI</span>
            </button>

            {/* 4. Language */}
            <button
                onClick={() => {
                    setIsLangSheetOpen(true);
                    setIsCategorySheetOpen(false);
                }}
                className={`${navItemClass} ${isLangSheetOpen ? 'text-accent' : ''}`}
            >
                <div className={pillClass(isLangSheetOpen)}>
                    <GlobeIcon className={iconClass(isLangSheetOpen)} />
                </div>
                <span className={labelClass}>{currentLanguage.toUpperCase()}</span>
            </button>

            {/* 5. Theme Toggle */}
            <button
                onClick={onThemeChange}
                className={`${navItemClass}`}
            >
                <div className={pillClass(false)}>
                    {currentTheme === 'light' ? (
                        <MoonIcon className={iconClass(false)} />
                    ) : (
                        <SunIcon className={iconClass(false)} />
                    )}
                </div>
                <span className={labelClass}>{t('dock.theme', currentLanguage)}</span>
            </button>

        </div>
      </div>

      {/* CATEGORY SHEET MODAL */}
      <AnimatePresence>
          {isCategorySheetOpen && (
              <motion.div
                  key="category-sheet-backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsCategorySheetOpen(false)}
                  className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-[100] cursor-pointer pointer-events-auto"
              />
          )}
          {isCategorySheetOpen && (
              <motion.div
                  key="category-sheet-content"
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="fixed bottom-0 left-0 right-0 z-[110] bg-[var(--bg-card)] rounded-t-[32px] max-h-[75vh] flex flex-col shadow-2xl border-t border-[var(--glass-border)] pb-24 pointer-events-auto" 
              >
                    <div className="flex items-center justify-between p-6 border-b border-[var(--bg-tertiary)] bg-[var(--bg-card)] rounded-t-[32px] sticky top-0 z-10">
                        <h2 className="text-xl font-serif font-bold text-[var(--text-primary)]">
                            {t('dock.menu', currentLanguage)}
                        </h2>
                        <button onClick={() => setIsCategorySheetOpen(false)} className="p-2 bg-[var(--bg-tertiary)] rounded-full hover:bg-[var(--accent-color)] hover:text-white transition-colors">
                            <CloseIcon className="w-6 h-6" />
                        </button>
                    </div>
                    <div className="overflow-y-auto p-4 space-y-2">
                        {menuData.map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => {
                                    onSelectCategory(cat.id);
                                    setIsCategorySheetOpen(false);
                                }}
                                className="w-full text-left flex items-center justify-between p-4 rounded-2xl bg-[var(--bg-primary)] border border-[var(--glass-border)] hover:border-[var(--accent-color)] active:scale-[0.98] transition-all"
                            >
                                <div>
                                    <span className="block text-base font-bold text-[var(--text-primary)]">{cat.names?.[currentLanguage] || cat.names?.ru}</span>
                                    {currentLanguage !== 'ru' && cat.names?.ru && (
                                        <span className="text-xs text-[var(--text-secondary)] italic">{cat.names.ru}</span>
                                    )}
                                </div>
                                <div className="bg-[var(--bg-tertiary)] text-[var(--text-secondary)] text-xs font-bold px-2 py-1 rounded-lg">
                                    {cat.items.length}
                                </div>
                            </button>
                        ))}
                    </div>
                  </motion.div>
          )}
      </AnimatePresence>

      {/* SERVICE SHEET MODAL */}
      <AnimatePresence>
          {isServiceSheetOpen && (
              <motion.div
                  key="service-sheet-backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsServiceSheetOpen(false)}
                  className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-[100] cursor-pointer pointer-events-auto"
              />
          )}
          {isServiceSheetOpen && (
              <motion.div
                  key="service-sheet-content"
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="fixed bottom-0 left-0 right-0 z-[110] bg-[var(--bg-card)] rounded-t-[32px] flex flex-col shadow-2xl border-t border-[var(--glass-border)] pb-24 pointer-events-auto"
              >
                    <div className="flex items-center justify-between p-6 border-b border-[var(--bg-tertiary)] bg-[var(--bg-card)] rounded-t-[32px]">
                        <h2 className="text-xl font-serif font-bold text-[var(--text-primary)]">
                            {t('dock.service', currentLanguage)}
                        </h2>
                        <button onClick={() => setIsServiceSheetOpen(false)} className="p-2 bg-[var(--bg-tertiary)] rounded-full hover:bg-[var(--accent-color)] hover:text-white transition-colors">
                            <CloseIcon className="w-6 h-6" />
                        </button>
                    </div>
                    <div className="p-4 grid grid-cols-2 gap-3">
                        {serviceActions.map((action) => (
                            <button
                                key={action.type}
                                onClick={() => handleServiceAction(action.type)}
                                className="flex flex-col items-center gap-2 p-5 rounded-2xl bg-[var(--bg-primary)] border border-[var(--glass-border)] hover:border-[var(--accent-20)] hover:shadow-md active:scale-[0.96] transition-all"
                            >
                                <div className={`w-12 h-12 ${action.color} rounded-2xl flex items-center justify-center`}>
                                    {action.icon}
                                </div>
                                <span className="font-bold text-sm text-[var(--text-primary)] text-center">{action.label}</span>
                            </button>
                        ))}
                    </div>
              </motion.div>
          )}
      </AnimatePresence>

      {/* LANGUAGE SHEET MODAL */}
      <AnimatePresence>
          {isLangSheetOpen && (
              <motion.div
                  key="lang-sheet-backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsLangSheetOpen(false)}
                  className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-[100] cursor-pointer pointer-events-auto"
              />
          )}
          {isLangSheetOpen && (
              <motion.div
                  key="lang-sheet-content"
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="fixed bottom-0 left-0 right-0 z-[110] bg-[var(--bg-card)] rounded-t-[32px] flex flex-col shadow-2xl border-t border-[var(--glass-border)] pb-24 pointer-events-auto"
              >
                     <div className="flex items-center justify-between p-6 border-b border-[var(--bg-tertiary)] bg-[var(--bg-card)] rounded-t-[32px]">
                        <h2 className="text-xl font-serif font-bold text-[var(--text-primary)]">
                            {t('dock.language', currentLanguage)}
                        </h2>
                        <button onClick={() => setIsLangSheetOpen(false)} className="p-2 bg-[var(--bg-tertiary)] rounded-full hover:bg-[var(--accent-color)] hover:text-white transition-colors">
                            <CloseIcon className="w-6 h-6" />
                        </button>
                    </div>
                    <div className="p-4 space-y-2">
                        {languages.map((lang) => (
                            <button
                                key={lang.code}
                                onClick={() => {
                                    onLanguageChange(lang.code);
                                    setIsLangSheetOpen(false);
                                }}
                                className={`w-full text-left flex items-center p-4 rounded-2xl transition-all ${
                                    currentLanguage === lang.code
                                        ? 'bg-[var(--accent-color)] text-white shadow-md'
                                        : 'bg-[var(--bg-primary)] text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]'
                                }`}
                            >
                                <span className="font-bold text-lg">{lang.name}</span>
                                {currentLanguage === lang.code && (
                                    <div className="ml-auto bg-white/20 p-1 rounded-full">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                  </motion.div>
          )}
      </AnimatePresence>
    </>
  );
};

export default BottomDock;
